import { PropsWithChildren, ReactNode } from 'react';

import { ExternalLink } from '@uikit/components/ExternalLink';
import LinkIcon from '@uikit/icons/Link';

import {
  REWARD_POOL_ACCESS_POLICY_TYPE,
  REWARD_POOL_TICKET_CURRENCY,
  RewardPoolTicketCurrencyType,
  SelfRewardPoolAccessPolicy,
} from '@shared/api/types/rewardPools';
import { DISCORD_URL } from '@shared/constants';
import { membershipTiers } from '@shared/constants/membershipTiers';
import { GENERAL_URLS } from '@shared/constants/urls';
import { pluralizeWord } from '@shared/helpers/formatters';
import { formatUsd } from '@shared/helpers/number';

export type RequirementItem = {
  id: string;
  title: ReactNode;
  description?: ReactNode;
  qualified: boolean;
  currencyType?: RewardPoolTicketCurrencyType;
  action?: {
    label: string;
    href: string;
  };
};

const storeBaseUrl = process.env.NEXT_PUBLIC_BASE_PATH || '';

interface ActionLinkProps {
  href?: string;
}

const RewardPoolPolicyActionLink = ({ href, children }: PropsWithChildren<ActionLinkProps>) => {
  return (
    <ExternalLink className="text-base-text-pink inline-flex gap-0.5 items-center" href={href}>
      {children}
    </ExternalLink>
  );
};

export const mapAccessPoliciesToRequirements = (accessPolicies: SelfRewardPoolAccessPolicy[]) => {
  const list: RequirementItem[] = [];

  accessPolicies.forEach((policy) => {
    switch (policy.name) {
      case REWARD_POOL_ACCESS_POLICY_TYPE.WHITELIST: {
        list.push({
          id: 'whitelist',
          title: 'Wallet Whitelist',
          description: policy.description,
          qualified: policy.qualified,
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.HOT_OFFERS_CLAIMED: {
        policy.hotOffersExpanded?.forEach(({ id, name, slug, isClaimed }) => {
          const href = `${storeBaseUrl}/hot-offers/${slug}`;

          list.push({
            id: `hot-offer-${id}`,
            title: 'Hot Offer Claimed',
            qualified: isClaimed,
            action: {
              label: 'Go to Hot Offer',
              href,
            },
            description: (
              <>
                Successfully claim{' '}
                <ExternalLink
                  className="text-base-text-red inline-flex gap-0.5 items-center"
                  href={href}
                >
                  {name} <LinkIcon className="icon-2xs" />
                </ExternalLink>
              </>
            ),
          });
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.KARMA_LIMIT: {
        list.push({
          id: `min-karma-${policy.options.minimal_karma}`,
          title: 'Minimum Karma',
          description: `Have a minimum total karma of ${policy.options.minimal_karma} Karma Points`,
          qualified: policy.qualified,
          action: {
            label: 'Earn Karma',
            href: `${storeBaseUrl}/profile/karma-rewards-guide`,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.MEMBERSHIP_TIERS: {
        const tierNames = policy.options.membership_tiers.map(
          (tierId) => membershipTiers[tierId].name,
        );
        const allTierNamesLabel =
          tierNames.length > 1
            ? `${tierNames.slice(0, -1).join(', ')} or ${tierNames[tierNames.length - 1]}`
            : tierNames[0];

        list.push({
          id: 'membership',
          title: 'Membership Tier',
          description: `Have ${allTierNamesLabel} Membership`,
          qualified: policy.qualified,
          action: {
            label: 'Magic Membership',
            href: `${storeBaseUrl}/profile/membership/plans`,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.HOT_OFFERS_AMOUNT: {
        const claimedAmount = policy.options.claimed_amount || 0;
        const url = `${storeBaseUrl}/hot-offers`;
        list.push({
          id: 'hot-offers-amount',
          title: 'Hot Offer Claimed in Number - All Time',
          description: (
            <>
              Claim a minimum of {claimedAmount}{' '}
              <RewardPoolPolicyActionLink href={url}>
                Hot Offer{claimedAmount !== 1 ? 's' : ''} <LinkIcon className="icon-2xs" />
              </RewardPoolPolicyActionLink>
            </>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Go to Hot Offers',
            href: url,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.APPLICATIONS_VOTED: {
        policy.appsExpanded?.forEach(({ id, name, slug }) => {
          const href = `${storeBaseUrl}/app/${slug}/validation`;

          list.push({
            id: `app-validation-${id}`,
            title: 'Validation - Specific Validation',
            qualified: policy.qualified,
            action: {
              label: 'Go to Validation',
              href,
            },
            description: (
              <>
                Participate in{' '}
                <RewardPoolPolicyActionLink href={href}>
                  {name} Validation <LinkIcon className="icon-2xs" />
                </RewardPoolPolicyActionLink>
              </>
            ),
          });
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.STAKED_SQR: {
        list.push({
          id: 'staked-sqr',
          title: 'Staking - Currently Staking',
          description: <>Stake minimum of {policy.options.amount} SQR amount</>,
          qualified: policy.qualified,
          action: {
            label: 'Go to Staking',
            href: GENERAL_URLS.staking,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.APPLICATIONS_VOTED_AMOUNT: {
        const href = `${storeBaseUrl}/profile/validation-tasks`;

        list.push({
          id: 'application-voted-amount',
          title: 'Validation - Number of Validations',
          description: (
            <>
              Participate in minimum {policy.options.amount}{' '}
              <RewardPoolPolicyActionLink href={href}>
                Project {pluralizeWord('Validation', policy.options.amount)}{' '}
                <LinkIcon className="icon-2xs" />
              </RewardPoolPolicyActionLink>
            </>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Go to Validations',
            href,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.USER_DAILY_LOGIN_AMOUNT: {
        list.push({
          id: 'user-daily-logins',
          title: 'Daily Logins',
          description: (
            <>
              Log in daily minimum of {policy.options.amount}{' '}
              {pluralizeWord('Time', policy.options.amount)}
            </>
          ),
          qualified: policy.qualified,
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.USER_ACCOUNT_AGE: {
        list.push({
          id: 'user-account-age',
          title: 'Age of Account Magic ID',
          description: (
            <>
              Have Magic ID account minimum {policy.options.daysAmount} of day
              {policy.options.daysAmount !== 1 ? 's' : ''} old
            </>
          ),
          qualified: policy.qualified,
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.SWAP_TOKEN_TOTAL: {
        list.push({
          id: 'swap-token-total',
          title: 'Token-Specific Swap Volume',
          description: (
            <>
              Execute swaps for Token {policy.options.tokenSymbol?.toUpperCase()} with a minimum
              total volume of {policy.options.minAmount} on Magic Swap
            </>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Go to Swap',
            href: process.env.NEXT_PUBLIC_SWAP_HOST || 'https://magicsquare.io/swap',
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.SWAP_TOTAL_IN_USD: {
        list.push({
          id: 'swap-token-total-in-usdt',
          title: 'General Swap Volume',
          description: (
            <>
              Complete token swaps with a cumulative total volume of at least{' '}
              {formatUsd(policy.options.minAmount)} on Magic Swap
            </>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Go to Swap',
            href: process.env.NEXT_PUBLIC_SWAP_HOST || 'https://magicsquare.io/swap',
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.USER_DISCORD_ROLE: {
        list.push({
          id: 'user-discord-role',
          title: 'Discord Role',
          description: (
            <>
              Hold specified Discord role
              {/* Hold {policy.options.rolesIds.join(', ')} Discord {pluralizeWord('role', policy.options.rolesIds.length)} */}
            </>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Go to Discord',
            href: DISCORD_URL,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.HOLDING_SQRP: {
        list.push({
          id: 'holding-sqrp',
          title: 'Holding X Amount SQRp',
          description: <>Hold minimum of {policy.options.amount} SQRp</>,
          qualified: policy.qualified,
          action: {
            label: 'Earn SQRp',
            href: GENERAL_URLS.staking,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.HOLDING_SQRV: {
        list.push({
          id: 'holding-sqrv',
          title: 'Holding X Amount SQRv',
          description: <>Hold minimum of {policy.options.amount} SQRv</>,
          qualified: policy.qualified,
          action: {
            label: 'Earn SQRv',
            href: 'https://docs.magicsquare.io/magic-dao/how-to-get-voting-power-in-the-magic-square-dao',
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.POW_OFFERS_AMOUNT: {
        const href = `${storeBaseUrl}/magic-board`;

        list.push({
          id: 'pow-offers-amount',
          title: 'Magic Board - Number of Tasks All Time',
          description: (
            <>
              Complete a minimum of {policy.options.claimed_amount}{' '}
              <RewardPoolPolicyActionLink href={href}>
                Magic Board Tasks <LinkIcon className="icon-2xs" />
              </RewardPoolPolicyActionLink>
            </>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Go to Magic Board',
            href,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.POW_OFFERS_CLAIMED: {
        policy.hotOffersExpanded?.forEach(({ id, name, slug }) => {
          const href = `${storeBaseUrl}/magic-board/${slug}`;

          list.push({
            id: `pow-offer-${id}`,
            title: 'Magic Board - Specific Task',
            qualified: policy.qualified,
            action: {
              label: 'Go to Task',
              href,
            },
            description: (
              <>
                Complete the{' '}
                <RewardPoolPolicyActionLink href={href}>
                  "{name}" <LinkIcon className="icon-2xs" />
                </RewardPoolPolicyActionLink>{' '}
                Magic Board Task
              </>
            ),
          });
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.REFERRED_AMOUNT: {
        list.push({
          id: 'referred-amount',
          title: 'Refer a Friend',
          description: <>Invite minimum of {policy.options.amount} friends</>,
          qualified: policy.qualified,
          action: {
            label: 'Invite Friends',
            href: `${storeBaseUrl}/refer-a-friend`,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.PROJECT_PARTICIPANT: {
        const launchpadHost = process.env.NEXT_PUBLIC_LAUNCHPAD_HOST;
        const projectSlug = policy.project?.slug || '';
        const href = `${launchpadHost}/token-sales/${projectSlug}`;
        const amount = policy?.options?.amount || 0;

        list.push({
          id: 'participation-ido',
          title: 'Participate in Specific Token Sale',
          description: (
            <>
              Participate in{' '}
              <RewardPoolPolicyActionLink href={href}>
                {policy.project?.name || 'N/A'} <LinkIcon className="icon-2xs" />
              </RewardPoolPolicyActionLink>{' '}
              {amount > 0 ? `with min deposit ${amount} USDT` : ''}
            </>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Go to Token Sale',
            href,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.DEPOSIT_VOLUME: {
        const launchpadHost = process.env.NEXT_PUBLIC_LAUNCHPAD_HOST;
        const href = launchpadHost;

        list.push({
          id: 'deposit-volume',
          title: 'Volume in Deposit in All IDOs',
          description: (
            <>
              Achieve minimum volume of {policy.options.amount} USDT deposits{' '}
              <RewardPoolPolicyActionLink href={href}>
                across all IDOs <LinkIcon className="icon-2xs" />
              </RewardPoolPolicyActionLink>
            </>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Go to Launchpad',
            href: process.env.NEXT_PUBLIC_LAUNCHPAD_HOST || '',
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.DEPOSIT_TURBO_CHARGE_VOLUME:
        list.push({
          id: 'turbo-charge',
          title: 'Used Turbo Charge',
          description: <>Use Turbo Charge with minimum {policy.options.amount}$ volume</>,
          qualified: policy.qualified,
          action: {
            label: 'Go to Launchpad',
            href: process.env.NEXT_PUBLIC_LAUNCHPAD_HOST || '',
          },
        });

        break;

      case REWARD_POOL_ACCESS_POLICY_TYPE.TICKET:
        policy.options.currencyTypes.forEach((currencyType) => {
          const { currency } = currencyType;
          const itemsMap = {
            FREE: {
              title: 'Use Free Raffle Credits',
              description:
                'Get tickets using your free ticket balance. More tickets - higher chance of winning!',
              label: 'Use Free Credits',
            },
            REST: {
              title: `Get Raffle Tickets with ${currency}`,
              description: `Use ${currency} from your Magic ID to enter the raffle.`,
              label: `Get with ${currency}`,
            },
          };

          const { title, description, label } =
            currency === REWARD_POOL_TICKET_CURRENCY.FREE ? itemsMap.FREE : itemsMap.REST;

          list.push({
            id: `ticket-${currency}`,
            title,
            description,
            qualified: policy.qualified,
            currencyType,
            action: {
              label,
              href: '',
            },
          });
        });

        break;

      case REWARD_POOL_ACCESS_POLICY_TYPE.LIVENESS_COMPLETED:
        list.push({
          id: `liveness-completed`,
          title: 'Complete Humanity Verification',
          description: (
            <span>
              Complete Liveness Verification or Gitcoin Passport in your{' '}
              <ExternalLink
                href="https://magicsquare.io/store/profile/magic-id/human-verification"
                className="text-base-text-red inline-flex gap-0.5 items-center"
              >
                Magic ID
              </ExternalLink>
              {'.'}
            </span>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Complete',
            href: 'https://magicsquare.io/store/profile/magic-id/human-verification',
          },
        });
        break;

      case REWARD_POOL_ACCESS_POLICY_TYPE.KYC_COMPLETED:
        list.push({
          id: `kyc-completed`,
          title: 'Complete KYC Verification',
          description: (
            <span>
              Complete KYC Verification in your{' '}
              <ExternalLink
                href="https://magicsquare.io/store/profile/magic-id/kyc-verification"
                className="text-base-text-red inline-flex gap-0.5 items-center"
              >
                Magic ID
              </ExternalLink>
              {'.'}
            </span>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Complete',
            href: 'https://magicsquare.io/store/profile/magic-id/kyc-verification',
          },
        });
        break;
    }
  });

  return list;
};
